import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    TextField,
    Avatar,
    Stack
} from '@mui/material';
import {
    ArrowLeft,
    ExternalLink,
    MessageCircle
} from 'lucide-react';
import { getBecomeHostById, updateBecomeHostStatus, assignBecomeHost } from '../../Services/BecomeHosts/BecomeHostsApi';
import { getBecomeHostsAdmins } from '../../Services/AdminUsers/AdminUsersApis';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import { useUser } from '../../context/UserContext';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';

const getStatusBadgeSx = (status) => {
    switch (status?.toLowerCase()) {
        case 'approved':
            return { bgcolor: '#E8F5E8', color: '#2E7D32', border: '1px solid #C8E6C9' };
        case 'rejected':
            return { bgcolor: '#FFEBEE', color: '#C62828', border: '1px solid #FFCDD2' };
        case 'under_review':
            return { bgcolor: '#F5F3FF', color: '#6B21A8', border: '1px solid #E9D5FF' };
        case 'contacted':
        case 're_submitted':
            return { bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' };
        case 'need_changes':
            return { bgcolor: '#FFF7ED', color: '#C2410C', border: '1px solid #FFEDD5' };
        case 'pending':
        default:
            return { bgcolor: '#FFF8E1', color: '#B45309', border: '1px solid #FFE082' };
    }
};

const BecomeHostDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showSnackbar } = useGlobalSnackbar();
    const { user } = useUser();

    const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = Boolean(
        currentUser?.role === 'Super Admin' ||
        currentUser?.roleName === 'Super Admin' ||
        currentUser?.roleId === 1 ||
        (typeof currentUser?.role === 'string' && currentUser.role.includes('Super Admin'))
    );

    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [adminUsersList, setAdminUsersList] = useState([]);

    // Reviewer Checklist state
    const [checklist, setChecklist] = useState({
        isWebsiteGenuine: false,
        isSocialProfileActive: false,
        isLocationReal: false,
        isNotDuplicate: false,
    });

    // Assignment state (stores saved admin ID and transient selection)
    const [assignedTo, setAssignedTo] = useState('unassigned');
    const [selectedAssignedTo, setSelectedAssignedTo] = useState('unassigned');

    // Rejection & Status state
    const [selectedStatus, setSelectedStatus] = useState('pending');
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionNotes, setRejectionNotes] = useState('');

    // Fetch details strictly by ID from API endpoint
    const fetchDetails = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const res = await getBecomeHostById(id);
            if (res && res.success && res.data) {
                setApplication(res.data);
                if (res.data.reviewChecklist) {
                    setChecklist({
                        isWebsiteGenuine: res.data.reviewChecklist.isWebsiteGenuine || false,
                        isSocialProfileActive: res.data.reviewChecklist.isSocialProfileActive || false,
                        isLocationReal: res.data.reviewChecklist.isLocationReal || false,
                        isNotDuplicate: res.data.reviewChecklist.isNotDuplicate || false,
                    });
                }
                if (res.data.assignedTo !== undefined && res.data.assignedTo !== null) {
                    setAssignedTo(res.data.assignedTo);
                    setSelectedAssignedTo(res.data.assignedTo);
                } else {
                    setAssignedTo('unassigned');
                    setSelectedAssignedTo('unassigned');
                }
                if (res.data.status) setSelectedStatus(res.data.status);
                if (res.data.rejectionReason) setRejectionReason(res.data.rejectionReason);
                if (res.data.rejectionNotes || res.data.remarks) setRejectionNotes(res.data.rejectionNotes || res.data.remarks);
            } else {
                setError("Application details not found.");
            }
        } catch (err) {
            console.error("Error fetching host application details:", err);
            setError(err.response?.data?.message || "Failed to load application details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchAdminUsers = useCallback(async () => {
        try {
            const res = await getBecomeHostsAdmins();
            if (res && res.success && Array.isArray(res.data)) {
                setAdminUsersList(res.data);
            }
        } catch (err) {
            console.error("Error fetching become hosts admin users:", err);
        }
    }, []);

    useEffect(() => {
        if (id) {
            fetchDetails();
        }
        if (isSuperAdmin) {
            fetchAdminUsers();
        }
    }, [id, fetchDetails, fetchAdminUsers, isSuperAdmin]);

    // Submit Assignment via PUT /become-hosts/:id/assign when user clicks Assign button
    const handleAssignSubmit = async () => {
        if (!id) return;
        setAssigning(true);
        try {
            let payload;
            if (selectedAssignedTo === 'unassigned' || selectedAssignedTo === '' || !selectedAssignedTo) {
                payload = { assignedTo: 'unassigned', assignedToName: null };
            } else {
                const targetAdmin = adminUsersList.find(a => String(a.id) === String(selectedAssignedTo));
                const adminName = targetAdmin ? (targetAdmin.name || targetAdmin.fullName) : '';
                payload = { assignedTo: Number(selectedAssignedTo), assignedToName: adminName };
            }

            const res = await assignBecomeHost(id, payload);
            if (res && res.success) {
                const newAssignedValue = payload.assignedTo === 'unassigned' ? 'unassigned' : Number(selectedAssignedTo);
                setAssignedTo(newAssignedValue);
                setSelectedAssignedTo(newAssignedValue);
                const assignedName = res.data?.assignedToName || payload.assignedToName || 'Unassigned';
                showSnackbar(res.message || `Application assigned to ${assignedName} successfully!`, "success");
            } else {
                showSnackbar(res?.message || "Failed to update assignment", "error");
            }
        } catch (err) {
            console.error("Error updating assignment:", err);
            showSnackbar(err.response?.data?.message || "Error assigning application", "error");
        } finally {
            setAssigning(false);
        }
    };

    // Revert selection when user clicks Cancel button
    const handleAssignCancel = () => {
        setSelectedAssignedTo(assignedTo);
    };

    // Handle checklist change
    const handleChecklistChange = (key) => (e) => {
        setChecklist(prev => ({
            ...prev,
            [key]: e.target.checked
        }));
    };

    // Handle Status Action (Approve / Reject / Update)
    const handleStatusUpdate = async (newStatus) => {
        if (!id) return;
        if (newStatus === 'rejected' && !rejectionReason) {
            showSnackbar("Please select a rejection reason before rejecting.", "warning");
            return;
        }
        setUpdating(true);
        try {
            const adminUser = user || JSON.parse(localStorage.getItem('user') || '{}');
            const payload = {
                status: newStatus,
                reviewChecklist: checklist,
                assignedToName: assignedTo,
                rejectionReason: newStatus === 'rejected' ? (rejectionReason || null) : null,
                rejectionNotes: rejectionNotes || null,
                remarks: rejectionNotes || null,
                updatedByName: adminUser?.name || adminUser?.fullName || adminUser?.email || 'Admin'
            };

            const res = await updateBecomeHostStatus(id, payload);
            if (res && res.success) {
                const formattedLabel = newStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                showSnackbar(`Host application status updated to "${formattedLabel}" successfully!`, "success");
                setApplication(prev => ({
                    ...prev,
                    ...res.data,
                    status: newStatus,
                    rejectionReason: newStatus === 'rejected' ? rejectionReason : null
                }));
            } else {
                showSnackbar(res?.message || "Failed to update status", "error");
            }
        } catch (err) {
            console.error("Error updating status:", err);
            showSnackbar(err.response?.data?.message || "Error updating application status", "error");
        } finally {
            setUpdating(false);
        }
    };

    // Helpers
    const getInitials = (name) => {
        if (!name) return 'HA';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const cleanPhone = (phone) => {
        if (!phone) return '';
        return phone.replace(/[^0-9]/g, '');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={32} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error || !application) {
        return (
            <Box sx={{ p: 4 }}>
                <Button
                    startIcon={<ArrowLeft size={16} />}
                    onClick={() => navigate('/host-applications')}
                    disableRipple disableFocusRipple
                    sx={{
                        color: '#64748B',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 400,
                        mb: 2,
                        '&:hover': { bgcolor: 'transparent', color: '#1E293B' }
                    }}
                >
                    Back to Host Applications
                </Button>
                <Alert severity="error" sx={{ borderRadius: '6px' }}>{error || "Application not found."}</Alert>
            </Box>
        );
    }

    const orgName = application.organizationName || 'Ananya Wellness Retreats';
    const contactName = application.contactPersonName || 'Ananya Sharma';
    const email = application.gmailId || application.email || 'ananya.retreats@gmail.com';
    const phoneNum = application.phone || '9876543210';
    const countryCode = application.countryCode || '+91';
    const fullPhone = `${countryCode} ${phoneNum}`;
    const website = application.website || 'ananyawellness.com';
    const socialMedia = application.socialMediaLink || 'instagram.com/ananyawellness';
    const locationStr = application.location || 'Rishikesh, Uttarakhand · India';
    const businessType = application.businessType || 'Retreat Center';
    const establishedYear = application.establishedYear || 2015;
    const offersList = Array.isArray(application.whatDoYouOffer) && application.whatDoYouOffer.length > 0
        ? application.whatDoYouOffer
        : ['Ayurveda', 'Yoga', 'Panchakarma', 'Meditation'];
    const message = application.additionalNotes || "We've run residential Ayurveda programmes in Rishikesh for 10 years and host mostly international guests. Happy to share our brochure and rate sheet. Best time to reach me is after 4pm.";
    const status = application.status || 'pending';

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>

                {/* Back Button & Header */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowLeft size={16} />}
                        onClick={() => navigate('/host-applications')}
                        disableRipple disableFocusRipple
                        sx={{
                            color: '#64748B',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 400,
                            p: 0,
                            mb: 1.5,
                            minWidth: 0,
                            '&:hover': { bgcolor: 'transparent', color: '#1E293B' }
                        }}
                    >
                        Back to Host Applications
                    </Button>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' } }}>
                                {orgName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.8rem', mt: 0.5, fontWeight: 400 }}>
                                Review the host application and verify retreat details before approving.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                ...getStatusBadgeSx(status),
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                borderRadius: '4px',
                                px: 1.5,
                                py: 0.5,
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}
                        >
                            {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </Box>
                    </Box>
                </Box>

                {/* Main 2-Column Layout */}
                <Grid container spacing={2.5}>

                    {/* Left Main Content Column */}
                    <Grid item size={{ xs: 12, lg: 8 }}>
                        <Stack spacing={2.5}>

                            {/* Card 1: Verification Details */}
                            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', p: 2.5 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                    Verification Details
                                </Typography>

                                <Grid container spacing={2}>
                                    {/* Website */}
                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Website</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Button
                                            component="a"
                                            href={website.startsWith('http') ? website : `https://${website}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            endIcon={<ExternalLink size={13} />}
                                            disableElevation disableRipple disableFocusRipple
                                            sx={{
                                                bgcolor: '#F8FAFC',
                                                color: '#1E293B',
                                                border: '1px solid #E2E8F0',
                                                textTransform: 'none',
                                                borderRadius: '4px',
                                                px: 1.25,
                                                py: 0.25,
                                                fontSize: '0.78rem',
                                                fontWeight: 400,
                                                '&:hover': { bgcolor: '#F1F5F9' }
                                            }}
                                        >
                                            {website.replace(/^https?:\/\//, '')}
                                        </Button>
                                    </Grid>

                                    {/* Social Media */}
                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Social Media</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Button
                                            component="a"
                                            href={socialMedia.startsWith('http') ? socialMedia : `https://${socialMedia}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            endIcon={<ExternalLink size={13} />}
                                            disableElevation disableRipple disableFocusRipple
                                            sx={{
                                                bgcolor: '#F8FAFC',
                                                color: '#1E293B',
                                                border: '1px solid #E2E8F0',
                                                textTransform: 'none',
                                                borderRadius: '4px',
                                                px: 1.25,
                                                py: 0.25,
                                                fontSize: '0.78rem',
                                                fontWeight: 400,
                                                '&:hover': { bgcolor: '#F1F5F9' }
                                            }}
                                        >
                                            {socialMedia.replace(/^https?:\/\//, '')}
                                        </Button>
                                    </Grid>

                                    {/* Phone / WhatsApp */}
                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Phone / WhatsApp</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                                {fullPhone}
                                            </Typography>
                                            <Button
                                                component="a"
                                                href={`https://wa.me/${cleanPhone(fullPhone)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                startIcon={<MessageCircle size={13} color="#2E7D32" />}
                                                disableElevation disableRipple disableFocusRipple
                                                sx={{
                                                    bgcolor: '#F8FAFC',
                                                    color: '#2E7D32',
                                                    border: '1px solid #E2E8F0',
                                                    textTransform: 'none',
                                                    borderRadius: '4px',
                                                    px: 1.25,
                                                    py: 0.25,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 400,
                                                    '&:hover': { bgcolor: '#F1F5F9' }
                                                }}
                                            >
                                                WhatsApp
                                            </Button>
                                        </Box>
                                    </Grid>

                                    {/* Gmail ID (login) */}
                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Gmail ID (Login)</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 400, color: '#1E293B' }}>
                                            {email}
                                        </Typography>
                                    </Grid>

                                    {/* Location */}
                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Location</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 400, color: '#1E293B' }}>
                                            {locationStr}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Card 2: Business Information */}
                            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', p: 2.5 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                    Business Information
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Organization / Retreat</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                            {orgName}
                                        </Typography>
                                    </Grid>

                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Contact Person</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 400, color: '#1E293B' }}>
                                            {contactName} · Founder
                                        </Typography>
                                    </Grid>

                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Business Type</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 400, color: '#1E293B' }}>
                                            {businessType}
                                        </Typography>
                                    </Grid>

                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Established</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 400, color: '#1E293B' }}>
                                            {establishedYear}
                                        </Typography>
                                    </Grid>

                                    <Grid item size={{ xs: 12, sm: 3.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Offers</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 8.5 }}>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                            {offersList.map((item, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={item}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#F8FAFC',
                                                        border: '1px solid #E2E8F0',
                                                        color: '#475569',
                                                        fontWeight: 400,
                                                        fontSize: '0.75rem',
                                                        borderRadius: '4px',
                                                        height: '24px'
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Card 3: Applicant's Message */}
                            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', p: 2.5 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mb: 1.5 }}>
                                    Applicant's Message
                                </Typography>

                                <Box
                                    sx={{
                                        bgcolor: '#F8FAFC',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '6px',
                                        p: 2
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#334155', fontStyle: 'italic', lineHeight: 1.6, fontSize: '0.82rem', fontWeight: 400 }}>
                                        "{message}"
                                    </Typography>
                                </Box>
                            </Paper>

                        </Stack>
                    </Grid>

                    {/* Right Sidebar Column */}
                    <Grid item size={{ xs: 12, lg: 4 }}>
                        <Stack spacing={2.5}>

                            {/* Card 1: Applicant Profile & Meta */}
                            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', p: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                    <Avatar sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 500, width: 40, height: 40, fontSize: '0.85rem', border: '1px solid #E2E8F0' }}>
                                        {getInitials(contactName)}
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ fontWeight: 500, color: '#1E293B', fontSize: '0.85rem' }}>
                                            {contactName}
                                        </Typography>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400, wordBreak: 'break-all' }}>
                                            {email}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 1.5, borderColor: '#F1F5F9' }} />

                                <Grid container spacing={1.25}>
                                    <Grid item size={{ xs: 6 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Application ID</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontWeight: 500, color: '#1E293B', fontSize: '0.78rem' }}>
                                            #HA-{application.id || '1042'}
                                        </Typography>
                                    </Grid>

                                    <Grid item size={{ xs: 5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Submitted</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 7 }} sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontWeight: 400, color: '#64748B', fontSize: '0.78rem' }}>
                                            {formatDate(application.createdAt)}
                                        </Typography>
                                    </Grid>

                                    <Grid item size={{ xs: 6 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 400 }}>Source</Typography>
                                    </Grid>
                                    <Grid item size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontWeight: 400, color: '#64748B', fontSize: '0.78rem' }}>
                                            Website form
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {isSuperAdmin && (
                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9' }}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748B', mb: 0.75 }}>
                                            Assigned Admin
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={selectedAssignedTo}
                                                onChange={(e) => setSelectedAssignedTo(e.target.value)}
                                                disabled={assigning}
                                                sx={{
                                                    borderRadius: '6px',
                                                    height: '36px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 400,
                                                    '& fieldset': { borderColor: '#E2E8F0' },
                                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }}
                                            >
                                                <MenuItem value="unassigned" sx={{ fontSize: '0.8rem' }}>Unassigned</MenuItem>
                                                {adminUsersList.map((admin) => (
                                                    <MenuItem key={admin.id} value={admin.id} sx={{ fontSize: '0.8rem' }}>
                                                        {admin.name || admin.fullName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {String(selectedAssignedTo) !== String(assignedTo) && (
                                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    fullWidth
                                                    disabled={assigning}
                                                    onClick={handleAssignSubmit}
                                                    disableElevation disableRipple disableFocusRipple
                                                    sx={{
                                                        bgcolor: '#FFBD65',
                                                        color: '#FFFFFF',
                                                        fontSize: '0.78rem',
                                                        fontWeight: 500,
                                                        textTransform: 'none',
                                                        borderRadius: '4px',
                                                        '&:hover': { bgcolor: '#F5A638' }
                                                    }}
                                                >
                                                    {assigning ? <CircularProgress size={16} color="inherit" /> : 'Assign'}
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    disabled={assigning}
                                                    onClick={handleAssignCancel}
                                                    disableElevation disableRipple disableFocusRipple
                                                    sx={{
                                                        color: '#64748B',
                                                        borderColor: '#E2E8F0',
                                                        fontWeight: 400,
                                                        fontSize: '0.78rem',
                                                        textTransform: 'none',
                                                        borderRadius: '4px',
                                                        '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' }
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Stack>
                                        )}
                                    </Box>
                                )}
                            </Paper>

                            {/* Card 2: Reviewer Checklist */}
                            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', p: 2.5 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mb: 1.5 }}>
                                    Reviewer Checklist
                                </Typography>

                                <Stack spacing={0.75}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checklist.isWebsiteGenuine}
                                                onChange={handleChecklistChange('isWebsiteGenuine')}
                                                size="small"
                                                sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 400 }}>Website opens & looks genuine</Typography>}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checklist.isSocialProfileActive}
                                                onChange={handleChecklistChange('isSocialProfileActive')}
                                                size="small"
                                                sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 400 }}>Social profile is active & matches</Typography>}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checklist.isLocationReal}
                                                onChange={handleChecklistChange('isLocationReal')}
                                                size="small"
                                                sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 400 }}>Location is a real place</Typography>}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checklist.isNotDuplicate}
                                                onChange={handleChecklistChange('isNotDuplicate')}
                                                size="small"
                                                sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 400 }}>Not a duplicate / existing partner</Typography>}
                                    />
                                </Stack>
                            </Paper>

                            {/* Card 3: Status Update Form */}
                            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', p: 2.5 }}>
                                <PermissionWrapper permission="edit">
                                    <Stack spacing={2}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>
                                            Update Application Status
                                        </Typography>

                                        <FormControl fullWidth size="small">
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400, mb: 0.75 }}>
                                                Select Status
                                            </Typography>
                                            <Select
                                                value={selectedStatus}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setSelectedStatus(val);
                                                    if (val !== 'rejected') {
                                                        setRejectionReason('');
                                                    }
                                                }}
                                                sx={{
                                                    borderRadius: '6px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 400,
                                                    height: '36px',
                                                    '& fieldset': { borderColor: '#E2E8F0' },
                                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }}
                                            >
                                                <MenuItem value="pending">
                                                    <Box sx={{ ...getStatusBadgeSx('pending'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Pending
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="under_review">
                                                    <Box sx={{ ...getStatusBadgeSx('under_review'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Under Review
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="contacted">
                                                    <Box sx={{ ...getStatusBadgeSx('contacted'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Contacted
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="re_submitted">
                                                    <Box sx={{ ...getStatusBadgeSx('re_submitted'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Re-Submitted
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="need_changes">
                                                    <Box sx={{ ...getStatusBadgeSx('need_changes'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Need Changes
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="approved">
                                                    <Box sx={{ ...getStatusBadgeSx('approved'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Approved
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="rejected">
                                                    <Box sx={{ ...getStatusBadgeSx('rejected'), px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                                        Rejected
                                                    </Box>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Rejection Reason (Shown only when status is 'rejected') */}
                                        {selectedStatus === 'rejected' && (
                                            <Box>
                                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400, mb: 0.75 }}>
                                                    Rejection Reason
                                                </Typography>

                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={rejectionReason}
                                                        onChange={(e) => setRejectionReason(e.target.value)}
                                                        displayEmpty
                                                        sx={{
                                                            borderRadius: '6px',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 400,
                                                            height: '36px',
                                                            '& fieldset': { borderColor: '#E2E8F0' },
                                                            '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                        }}
                                                    >
                                                        <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Select reason...</MenuItem>
                                                        <MenuItem value="Could not verify — no working website/social" sx={{ fontSize: '0.8rem' }}>Could not verify — no working website/social</MenuItem>
                                                        <MenuItem value="Incomplete or unclear details" sx={{ fontSize: '0.8rem' }}>Incomplete or unclear details</MenuItem>
                                                        <MenuItem value="Duplicate application" sx={{ fontSize: '0.8rem' }}>Duplicate application</MenuItem>
                                                        <MenuItem value="Not a fit for the platform" sx={{ fontSize: '0.8rem' }}>Not a fit for the platform</MenuItem>
                                                        <MenuItem value="Other" sx={{ fontSize: '0.8rem' }}>Other</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}

                                        {/* Notes / Remarks Section */}
                                        <Box>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400, mb: 0.75 }}>
                                                Notes / Remarks
                                            </Typography>
                                            <TextField
                                                placeholder="Optional note (kept for internal records)"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={rejectionNotes}
                                                onChange={(e) => setRejectionNotes(e.target.value)}
                                                sx={{
                                                    mb: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.8rem',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                        </Box>

                                        {/* Update Button */}
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            disabled={updating}
                                            onClick={() => handleStatusUpdate(selectedStatus)}
                                            disableElevation disableRipple disableFocusRipple
                                            sx={{
                                                bgcolor: '#FFBD65',
                                                color: '#FFFFFF',
                                                fontWeight: 500,
                                                fontSize: '0.85rem',
                                                py: 1,
                                                borderRadius: '6px',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    bgcolor: '#F5A638'
                                                }
                                            }}
                                        >
                                            {updating ? <CircularProgress size={20} color="inherit" /> : 'Update Status'}
                                        </Button>

                                        <Typography sx={{ color: '#94A3B8', fontSize: '0.72rem', display: 'block', textAlign: 'center', mt: 0.5, fontWeight: 400 }}>
                                            Approval unlocks the host's "Add Retreat" access.
                                        </Typography>

                                    </Stack>
                                </PermissionWrapper>
                            </Paper>

                        </Stack>
                    </Grid>

                </Grid>

            </Box>
        </PermissionWrapper>
    );
};

export default BecomeHostDetails;
